/* Keep each buy box independent, including Shopify theme editor section reloads. */
if (!customElements.get('cw-build-bundle')) {
  customElements.define('cw-build-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.busy = false;
      this.selectedGift = null;
      this.threshold = Math.max(1, Number(this.dataset.threshold) || 2);
      this.giftQuantity = Math.max(1, Number(this.dataset.giftQuantity) || 1);
      this.offerEnabled = this.dataset.giftEnabled === 'true';
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: this.dataset.currency || 'GBP'
      });
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this, 'input', event => {
        if (event.target.matches('.cw_current_product input[type="number"]')) this.update();
      });
      this.listen(this, 'keydown', event => {
        if (event.target.matches('#toggleSwitch') && ['Enter', ' '].includes(event.key)) {
          event.preventDefault();
          event.target.click();
        }
      });
      const toggle = this.querySelector('#toggleSwitch');
      if (toggle) {
        toggle.tabIndex = 0;
        toggle.setAttribute('role', 'switch');
        toggle.setAttribute('aria-label', 'Subscribe and save');
        toggle.setAttribute('aria-checked', String(toggle.classList.contains('active')));
      }
      this.viewport = this.querySelector('.gwp-carousel__viewport');
      if (this.viewport) {
        this.listen(this.viewport, 'scroll', () => this.updateCarousel());
        this.resizeObserver = new ResizeObserver(() => this.updateCarousel());
        this.resizeObserver.observe(this.viewport);
      }
      this.listen(window, 'scroll', () => this.updateSticky(), { passive: true });
      this.listen(window, 'resize', () => this.updateSticky(), { passive: true });
      this.initSwiper();
      if (document.readyState === 'loading') this.listen(document, 'DOMContentLoaded', () => this.initSwiper());
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    listen(target, name, handler, options = {}) {
      target.addEventListener(name, handler, { ...options, signal: this.controller.signal });
    }

    initSwiper() {
      const element = this.querySelector('.cw-product-swiper');
      if (!element || this.swiper || typeof Swiper === 'undefined') return;
      this.swiper = new Swiper(element, {
        slidesPerView: 3.3, spaceBetween: 10, loop: false,
        navigation: { nextEl: element.querySelector('.swiper-button-next'), prevEl: element.querySelector('.swiper-button-prev') },
        pagination: { el: element.querySelector('.swiper-pagination'), clickable: true },
        breakpoints: { 0: { slidesPerView: 2.3, spaceBetween: 8 }, 768: { slidesPerView: 3.3, spaceBetween: 10 } }
      });
    }

    state() {
      const items = [];
      let count = 0, original = 0, flavour = 0, otherTotal = 0, compare = 0;
      const subscribed = this.querySelector('#toggleSwitch')?.classList.contains('active');
      this.querySelectorAll('.cw_current_product').forEach(card => {
        const input = card.querySelector('input[type="number"]');
        if (!input) return;
        const quantity = card.classList.contains('sold_out') ? 0 : Math.max(0, Math.floor(Number(input.value) || 0));
        input.value = quantity;
        input.min = 0;
        input.step = 1;
        const controls = card.querySelector('.data-btns');
        const quick = card.querySelector('.first_btns_selector');
        if (controls) controls.style.display = quantity ? 'flex' : 'none';
        if (quick) {
          quick.style.display = quantity ? 'none' : '';
          quick.disabled = card.classList.contains('sold_out');
        }
        if (!quantity) return;
        const price = Number(card.dataset.price) || 0;
        count += quantity;
        compare += price * quantity;
        if (price === 3000) original += quantity;
        else if (price === 3200) flavour += quantity;
        else otherTotal += price * quantity;
        const item = { id: input.dataset.productId, quantity };
        if (subscribed && input.dataset.sellingId) item.selling_plan = input.dataset.sellingId;
        items.push(item);
      });
      let total = compare;
      // Preserve the store's existing ladder schedule for its supported bottle prices.
      if (this.dataset.ladderPricing === 'true' && original + flavour > 0) {
        const ladderCount = original + flavour;
        const table = flavour ? [0, 3200, 6000, 9000, 11500, 14000, 16500] : [0, 3000, 5600, 8200, 10800, 13400, 15000];
        total = otherTotal + (ladderCount <= 6 ? table[ladderCount] : (flavour ? original * 2550 + flavour * 2750 : original * 2500));
      }
      const eligible = this.offerEnabled && count >= this.threshold;
      return { items, count, total, compare, eligible };
    }

    formatCopy(template, state) {
      const remaining = Math.max(0, this.threshold - state.count);
      const values = {
        remaining,
        remaining_bottles: `${remaining} more bottle${remaining === 1 ? '' : 's'}`,
        threshold: this.threshold,
        quantity: this.giftQuantity,
        count: state.count,
        gift_offer: this.giftQuantity === 1 ? 'a free gin gift' : `${this.giftQuantity} free gin gifts`
      };
      return template.replace(/\{(remaining|remaining_bottles|threshold|quantity|count|gift_offer)\}/g, (_, key) => values[key]);
    }

    update() {
      const state = this.state();
      if (!state.eligible) this.selectedGift = null;
      if (typeof window.updateGinGiftProgressPreview === 'function') {
        window.updateGinGiftProgressPreview(state.count);
      }
      const gifts = [...this.querySelectorAll('[data-gift-id]')];
      const giftRequired = state.eligible && gifts.length > 0;
      gifts.forEach(gift => {
        const selected = gift === this.selectedGift;
        gift.disabled = !state.eligible || this.busy;
        gift.classList.toggle('is-selected', selected);
        gift.setAttribute('aria-pressed', String(selected));
        gift.querySelector('[data-gift-button-label]').textContent = selected ? this.dataset.selectedButton : this.dataset.selectLabel;
      });
      const copy = this.formatCopy(this.selectedGift ? this.dataset.selectedLabel : state.eligible ? this.dataset.unlockedLabel : this.dataset.lockedLabel, state);
      const bottleOffer = this.querySelector('[data-bottle-offer-copy]');
      if (bottleOffer) bottleOffer.textContent = this.formatCopy(this.dataset.bottleOffer, state);
      this.querySelectorAll('[data-offer-progress-copy], [data-gift-status]').forEach(el => {
        el.textContent = gifts.length ? copy : (this.querySelector('[data-gifts-empty]')?.textContent || '');
      });
      const giftValue = this.selectedGift ? Math.max(0, Number(this.selectedGift.dataset.giftPrice) || 0) : 0;
      const displayCompare = state.compare + giftValue;
      const displaySaving = Math.max(0, displayCompare - state.total);
      const label = !state.count ? this.dataset.emptyLabel : giftRequired && !this.selectedGift ? this.dataset.giftRequired : this.dataset.atcLabel;
      this.querySelectorAll('.main_bundle_adding button, [data-sticky-submit]').forEach(button => {
        button.disabled = this.busy || !state.count;
        if (button.matches('[data-sticky-submit]')) button.textContent = label;
        else button.querySelector('.init_sm_btn').textContent = label;
      });
      this.querySelectorAll('.cr_price_init, [data-sticky-total]').forEach(el => el.textContent = this.money.format(state.total / 100));
      this.querySelectorAll('.init_pricing').forEach(el => el.style.display = state.count ? 'flex' : 'none');
      this.querySelectorAll('.cr_all_savings').forEach(el => {
        el.style.display = displaySaving > 0 ? 'flex' : 'none';
        const compare = el.querySelector('.cr_compare_init');
        const saving = el.querySelector('.savings_mn');
        if (compare) compare.textContent = this.money.format(displayCompare / 100);
        if (saving) saving.textContent = `Save ${this.money.format(displaySaving / 100)}`;
      });
      const selection = this.querySelector('[data-sticky-selection]');
      if (selection) selection.textContent = `${state.count} bottle${state.count === 1 ? '' : 's'} selected`;
      const giftSummary = this.querySelector('[data-sticky-gift]');
      if (giftSummary) giftSummary.textContent = this.selectedGift ? `${this.giftQuantity} × ${this.selectedGift.dataset.giftTitle}` : this.offerEnabled && gifts.length ? copy : '';
      this.updateCarousel();
      this.updateSticky();
    }

    updateSticky() {
      const sticky = this.querySelector('.gwp-bundle__sticky-footer');
      const bundle = this.querySelector('.cw_build_bundle');
      const submit = this.querySelector('.main_bundle_adding');
      let visible = false;
      if (sticky && bundle && submit) {
        const rect = submit.getBoundingClientRect();
        visible = bundle.getBoundingClientRect().top < window.innerHeight && !(rect.top >= 0 && rect.bottom <= window.innerHeight);
        sticky.hidden = !visible;
        sticky.classList.toggle('if-sticky-view', visible);
      }
      const defaultAtc = this.querySelector('.add_cart_function');
      const inBundle = bundle && bundle.getBoundingClientRect().top < window.innerHeight && bundle.getBoundingClientRect().bottom > 0;
      defaultAtc?.classList.toggle('fixed_stikcy_cw', window.scrollY >= 500 && !inBundle && !visible);
    }

    updateCarousel() {
      if (!this.viewport) return;
      const pagination = this.querySelector('.gwp-carousel__pagination');
      if (!pagination) return;
      const width = this.viewport.clientWidth;
      if (!width) return;
      const max = Math.max(0, this.viewport.scrollWidth - width);
      const pages = Math.ceil(max / width) + 1;
      const active = max > 0 && this.viewport.scrollLeft >= max - 1
        ? pages - 1
        : Math.min(pages - 1, Math.round(this.viewport.scrollLeft / width));
      if (pagination.children.length !== pages) {
        pagination.replaceChildren(...Array.from({ length: pages }, (_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'gwp-carousel__bullet';
          button.dataset.giftPage = index;
          button.setAttribute('aria-label', `Gift page ${index + 1}`);
          return button;
        }));
      }
      [...pagination.children].forEach((button, index) => {
        button.classList.toggle('is-active', index === active);
        button.setAttribute('aria-current', index === active ? 'true' : 'false');
      });
      this.querySelector('[data-gift-prev]').disabled = this.viewport.scrollLeft <= 1;
      this.querySelector('[data-gift-next]').disabled = this.viewport.scrollLeft >= max - 1;
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (button?.disabled) return;
      const card = button?.closest('.cw_current_product');
      if (card) {
        const input = card.querySelector('input[type="number"]');
        if (button.matches('.first_btns_selector')) input.value = 1;
        else if (button.closest('.data-btns')) input.value = Math.max(0, (Number(input.value) || 0) + (button === button.parentElement.querySelector('button') ? -1 : 1));
        this.update();
      }
      if (button?.matches('[data-gift-id]') && this.state().eligible) {
        this.selectedGift = button;
        this.update();
      }
      if (button?.matches('[data-gift-prev], [data-gift-next], [data-gift-page]')) {
        const width = this.viewport.clientWidth;
        const left = button.hasAttribute('data-gift-page') ? Number(button.dataset.giftPage) * width : this.viewport.scrollLeft + (button.hasAttribute('data-gift-prev') ? -width : width);
        this.viewport.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
      if (button?.matches('.main_bundle_adding button, [data-sticky-submit]')) this.submit(button, true);
      if (button?.matches('.main_atc_button')) this.submit(button, false);
      const toggle = event.target.closest('#toggleSwitch');
      if (toggle) {
        toggle.classList.toggle('active');
        toggle.setAttribute('aria-checked', String(toggle.classList.contains('active')));
      }
      if (event.target.closest('.promo_subtitle a')) {
        event.preventDefault();
        const content = this.querySelector('.subscription_works');
        if (content) content.style.display = getComputedStyle(content).display === 'none' ? 'block' : 'none';
      }
      if (event.target.closest('.ticket_free_ds')) {
        const details = document.querySelector('.ds-ricky-gervais-mortality-competition');
        if (details) { details.open = true; details.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      }
    }

    async submit(button, isBundle) {
      if (this.busy) return;
      const state = this.state();
      let items;
      if (isBundle) {
        if (!state.count) return;
        if (state.eligible && this.querySelector('[data-gift-id]') && !this.selectedGift) {
          const gifts = this.querySelector('.cw-bundle-gifts');
          gifts?.focus({ preventScroll: true });
          gifts?.scrollIntoView({
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
          return;
        }
        items = state.items;
        if (state.eligible && this.selectedGift) {
          items.push({ id: this.selectedGift.dataset.giftId, quantity: this.giftQuantity, properties: { _data: 'freegift' } });
        }
      } else {
        if (!button.dataset.id) return;
        const input = this.querySelector('.add_cart_function input[name="quantity"]');
        if (input && !input.reportValidity()) return;
        items = [{ id: button.dataset.id, quantity: Math.max(1, Math.floor(Number(input?.value) || 1)) }];
        if (this.querySelector('#toggleSwitch')?.classList.contains('active') && button.dataset.sellingId) items[0].selling_plan = button.dataset.sellingId;
      }
      const error = this.querySelector('[data-bundle-error]');
      error.hidden = true;
      this.busy = true;
      button.classList.add('active_loader');
      this.setAttribute('aria-busy', 'true');
      this.update();
      let ginGiftFreshHTML = null;
      let ginGiftProgress = null;
      try {
        ginGiftProgress = document.getElementById('PDP-Gin-Gift-Progress');
        const ginGiftSectionId = ginGiftProgress?.dataset.sectionId;
        const payload = { items };
        if (ginGiftSectionId) payload.sections = [ginGiftSectionId];

        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Unable to add your selection. Please try again.');

        if (ginGiftProgress && ginGiftSectionId && result.sections && result.sections[ginGiftSectionId]) {
          const fresh = new DOMParser()
            .parseFromString(result.sections[ginGiftSectionId], 'text/html')
            .getElementById('PDP-Gin-Gift-Progress');
          if (fresh) ginGiftFreshHTML = fresh.innerHTML;
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.querySelector('#cart-drawer-toggle')?.click();
      } catch (exception) {
        error.textContent = exception.message || 'Unable to add your selection. Please try again.';
        error.hidden = false;
        error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        button.classList.remove('active_loader');
        this.update();
        // Applied after update() so the real, server-confirmed cart state
        // (including the new baseline) wins over the widget's own
        // not-yet-cleared quantity preview.
        if (ginGiftFreshHTML !== null && ginGiftProgress) {
          ginGiftProgress.innerHTML = ginGiftFreshHTML;
        }
      }
    }
  });
}
